'use client';

import { c as _c } from "react/compiler-runtime";
import { domMax, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { UserIdentity } from '@/components/profile/user-identity';
import { ReactionVoteButtons } from '@/components/shared/reaction-vote-buttons';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { Tick01IconSolid } from '@/components/ui/icon/glyphs';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { logger } from '@/lib/telemetry/logger';
import { MOTION_POSITION } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
import type { SuggestionWire } from './types';
import { effectiveImageUrl, effectiveLabel, effectiveTicker } from './types';
import { useSuggestionVoteModel } from './use-suggestion-vote-model';
interface SuggestionFeedRowProps {
  suggestion: SuggestionWire;
  isCreator?: boolean;
  isPromoted: boolean;
  reservedOutcomeIndex?: number;
  showSeparator?: boolean;
  onAddToMarket?: (() => void) | undefined;
}
export function SuggestionFeedRow(t0) {
  const $ = _c(80);
  const {
    suggestion,
    isCreator: t1,
    isPromoted,
    reservedOutcomeIndex,
    showSeparator: t2,
    onAddToMarket
  } = t0;
  const isCreator = t1 === undefined ? false : t1;
  const showSeparator = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== suggestion) {
    t3 = effectiveLabel(suggestion);
    $[0] = suggestion;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const label = t3;
  let t4;
  if ($[2] !== suggestion) {
    t4 = effectiveTicker(suggestion);
    $[2] = suggestion;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const ticker = t4;
  let t5;
  if ($[4] !== suggestion) {
    t5 = effectiveImageUrl(suggestion);
    $[4] = suggestion;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const imageUrl = t5;
  let t6;
  if ($[6] !== suggestion.suggesterPubkey) {
    t6 = suggestion.suggesterPubkey.slice(0, 4);
    $[6] = suggestion.suggesterPubkey;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== suggestion.suggesterPubkey) {
    t7 = suggestion.suggesterPubkey.slice(-4);
    $[8] = suggestion.suggesterPubkey;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const truncatedPubkey = `${t6}…${t7}`;
  let t8;
  if ($[10] !== suggestion.suggester.connectedProviders) {
    t8 = suggestion.suggester.connectedProviders ?? [];
    $[10] = suggestion.suggester.connectedProviders;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const connectedProviders = t8;
  const hasReason = suggestion.reason !== null && suggestion.reason.length > 0;
  const prefersReducedMotion = useReducedMotion();
  const outcomeIndex = suggestion.status === "promoting" ? reservedOutcomeIndex ?? null : suggestion.outcomeIndex;
  let t9;
  if ($[12] !== outcomeIndex) {
    t9 = Number.isInteger(outcomeIndex) && outcomeIndex !== null && outcomeIndex >= 0 && outcomeIndex < 10;
    $[12] = outcomeIndex;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const hasValidOutcomeIndex = t9;
  let t10;
  if ($[14] !== hasValidOutcomeIndex || $[15] !== label || $[16] !== outcomeIndex) {
    t10 = hasValidOutcomeIndex ? [{
      title: label,
      index: outcomeIndex
    }] : [];
    $[14] = hasValidOutcomeIndex;
    $[15] = label;
    $[16] = outcomeIndex;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const identityColors = useOutcomeColor(t10);
  const identityColor = identityColors[0] ?? null;
  let t11;
  let t12;
  if ($[18] !== hasValidOutcomeIndex || $[19] !== suggestion.id || $[20] !== suggestion.status) {
    t11 = () => {
      if (suggestion.status !== "promoted" || hasValidOutcomeIndex) {
        return;
      }
      logger.warn("suggested_outcomes.promoted_index_missing", {
        suggestionId: suggestion.id,
        status: suggestion.status
      });
    };
    t12 = [hasValidOutcomeIndex, suggestion.id, suggestion.status];
    $[18] = hasValidOutcomeIndex;
    $[19] = suggestion.id;
    $[20] = suggestion.status;
    $[21] = t11;
    $[22] = t12;
  } else {
    t11 = $[21];
    t12 = $[22];
  }
  useEffect(t11, t12);
  const t13 = `suggestion-row-${suggestion.id}`;
  let t14;
  if ($[23] !== prefersReducedMotion) {
    t14 = prefersReducedMotion === true ? {
      duration: 0
    } : MOTION_POSITION;
    $[23] = prefersReducedMotion;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const t15 = isPromoted ? "true" : "false";
  const t16 = !isPromoted && showSeparator && "border-t border-border-secondary";
  const t17 = isPromoted && "rounded-panel-row border px-3";
  const t18 = isPromoted && identityColor === null && "border-border-secondary bg-surface-secondary";
  const t19 = isPromoted && identityColor?.outline;
  const t20 = isPromoted && identityColor !== null && `border-outcome-${identityColor.hue}-fill`;
  let t21;
  if ($[25] !== t16 || $[26] !== t17 || $[27] !== t18 || $[28] !== t19 || $[29] !== t20) {
    t21 = cn("grid grid-cols-[36px_1fr_auto] items-start gap-3 py-3", t16, t17, t18, t19, t20);
    $[25] = t16;
    $[26] = t17;
    $[27] = t18;
    $[28] = t19;
    $[29] = t20;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  let t22;
  if ($[31] !== imageUrl || $[32] !== label) {
    t22 = <OptimizedImage variant="thumbnail" src={imageUrl} alt={label} size="size-9" sizes="36px" rounded="rounded-item" className="block size-9 shrink-0 self-start" imageClassName="size-9 object-cover" />;
    $[31] = imageUrl;
    $[32] = label;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== label) {
    t23 = <span className="truncate text-body font-medium text-foreground-primary">{label}</span>;
    $[34] = label;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  let t24;
  if ($[36] !== ticker) {
    t24 = <span className="shrink-0 font-mono text-caption text-foreground-secondary">${ticker}</span>;
    $[36] = ticker;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  let t25;
  if ($[38] !== t23 || $[39] !== t24) {
    t25 = <div className="flex items-baseline gap-2">{t23}{t24}</div>;
    $[38] = t23;
    $[39] = t24;
    $[40] = t25;
  } else {
    t25 = $[40];
  }
  let t26;
  if ($[41] !== connectedProviders || $[42] !== suggestion.suggester) {
    t26 = {
      ...suggestion.suggester,
      connectedProviders
    };
    $[41] = connectedProviders;
    $[42] = suggestion.suggester;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  let t27;
  if ($[44] !== t26 || $[45] !== truncatedPubkey) {
    t27 = <UserIdentity user={t26} variant="inline" size="sm" fallback={truncatedPubkey} />;
    $[44] = t26;
    $[45] = truncatedPubkey;
    $[46] = t27;
  } else {
    t27 = $[46];
  }
  let t28;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <span aria-hidden="true" className="shrink-0">·</span>;
    $[47] = t28;
  } else {
    t28 = $[47];
  }
  let t29;
  if ($[48] !== suggestion.createdAt) {
    t29 = relativeTime(suggestion.createdAt);
    $[48] = suggestion.createdAt;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  let t30;
  if ($[50] !== t29) {
    t30 = <span className="shrink-0">{t29}</span>;
    $[50] = t29;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] !== t27 || $[53] !== t30) {
    t31 = <div className="flex min-w-0 items-center gap-1.5 text-caption text-foreground-tertiary">{t27}{t28}{t30}</div>;
    $[52] = t27;
    $[53] = t30;
    $[54] = t31;
  } else {
    t31 = $[54];
  }
  let t32;
  if ($[55] !== hasReason || $[56] !== suggestion.reason) {
    t32 = hasReason ? <p className="line-clamp-2 break-words text-caption text-foreground-secondary">{suggestion.reason}</p> : null;
    $[55] = hasReason;
    $[56] = suggestion.reason;
    $[57] = t32;
  } else {
    t32 = $[57];
  }
  let t33;
  if ($[58] !== t25 || $[59] !== t31 || $[60] !== t32) {
    t33 = <div className="flex min-w-0 flex-col gap-1 self-start">{t25}{t31}{t32}</div>;
    $[58] = t25;
    $[59] = t31;
    $[60] = t32;
    $[61] = t33;
  } else {
    t33 = $[61];
  }
  const t34 = isPromoted ? "self-center" : "self-start";
  let t35;
  if ($[62] !== t34) {
    t35 = cn("flex items-center gap-2", t34);
    $[62] = t34;
    $[63] = t35;
  } else {
    t35 = $[63];
  }
  let t36;
  if ($[64] !== isCreator || $[65] !== isPromoted || $[66] !== onAddToMarket || $[67] !== suggestion) {
    t36 = isPromoted ? <CreatorStatus status={suggestion.status} /> : <ActiveRowActions suggestion={suggestion} isCreator={isCreator} onAddToMarket={onAddToMarket} />;
    $[64] = isCreator;
    $[65] = isPromoted;
    $[66] = onAddToMarket;
    $[67] = suggestion;
    $[68] = t36;
  } else {
    t36 = $[68];
  }
  let t37;
  if ($[69] !== t35 || $[70] !== t36) {
    t37 = <div className={t35}>{t36}</div>;
    $[69] = t35;
    $[70] = t36;
    $[71] = t37;
  } else {
    t37 = $[71];
  }
  let t38;
  if ($[72] !== t13 || $[73] !== t14 || $[74] !== t15 || $[75] !== t21 || $[76] !== t22 || $[77] !== t33 || $[78] !== t37) {
    t38 = <LazyMotion features={domMax}><m.div layout={true} layoutId={t13} transition={t14} data-promoted={t15} className={t21}>{t22}{t33}{t37}</m.div></LazyMotion>;
    $[72] = t13;
    $[73] = t14;
    $[74] = t15;
    $[75] = t21;
    $[76] = t22;
    $[77] = t33;
    $[78] = t37;
    $[79] = t38;
  } else {
    t38 = $[79];
  }
  return t38;
}
function CreatorStatus(t0) {
  const $ = _c(8);
  const {
    status
  } = t0;
  const t = useTranslations("launchpad.suggestedOutcomes");
  let t1;
  if ($[0] !== status) {
    t1 = status === "promoted" ? <Icon icon={Tick01IconSolid} className="size-3.5" /> : null;
    $[0] = status;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== status || $[3] !== t) {
    t2 = status === "promoted" ? t("addedByCreator") : t("creator.adding");
    $[2] = status;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <span className="inline-flex items-center gap-1.5 rounded-pill border border-feedback-success-border bg-feedback-success-surface px-2.5 py-1 text-caption font-semibold text-feedback-success-content">{t1}{t2}</span>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function ActiveRowActions(t0) {
  const $ = _c(15);
  const {
    suggestion,
    isCreator,
    onAddToMarket
  } = t0;
  const t = useTranslations("launchpad.suggestedOutcomes");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      disabled: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const voteModel = useSuggestionVoteModel(suggestion.id, suggestion, t1);
  let t2;
  if ($[1] !== voteModel.downAriaLabel || $[2] !== voteModel.downCount || $[3] !== voteModel.onReact || $[4] !== voteModel.reaction || $[5] !== voteModel.upAriaLabel || $[6] !== voteModel.upCount) {
    t2 = <ReactionVoteButtons reaction={voteModel.reaction} upCount={voteModel.upCount} downCount={voteModel.downCount} upAriaLabel={voteModel.upAriaLabel} downAriaLabel={voteModel.downAriaLabel} onReact={voteModel.onReact} size="sm" />;
    $[1] = voteModel.downAriaLabel;
    $[2] = voteModel.downCount;
    $[3] = voteModel.onReact;
    $[4] = voteModel.reaction;
    $[5] = voteModel.upAriaLabel;
    $[6] = voteModel.upCount;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== isCreator || $[9] !== onAddToMarket || $[10] !== t) {
    t3 = isCreator && onAddToMarket !== undefined ? <AnimatedButton type="button" variant="primary" size="sm" onClick={onAddToMarket}>{t("addToMarket")}</AnimatedButton> : null;
    $[8] = isCreator;
    $[9] = onAddToMarket;
    $[10] = t;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t2 || $[13] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
function relativeTime(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const deltaSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)}h ago`;
  return `${Math.floor(deltaSec / 86400)}d ago`;
}